/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

export type NodeId = string;
export type NodeConsistentId = string;

export interface ClusterNode {
  nodeId: NodeId | null;
  consistentId: NodeConsistentId;
  client: boolean;
  baselineNode: boolean;
  online: boolean;
  order: number;
  attributes: {
    'org.apache.ignite.build.ver': string;
    'org.apache.ignite.ips': string;
    'org.apache.ignite.jvm.pid': number;
    'org.apache.ignite.macs': string;
    'org.apache.ignite.features'?: string;
    'plugins.gridgain.control.center.agent.present'?: boolean;
    'org.apache.ignite.ignite.name'?: string;
  };
  drReceiverHubNode: boolean;
  drReceiverInboundPort: number | null;
  drSenderHubNode: boolean;
  drSenderGroups: string[];
  hostNames: string[];
}

export interface TopologySnapshot {
  topologyVersion: number;
  coordinatorConsistentId: string | null;
  nodes: ClusterNode[];
}

export enum NodeBulkSelector {
  ALL = '*',
  CLIENT = 'CLIENT',
  SERVER = 'SERVER',
}

export interface FailureHandler {
  ignoredFailureTypes: string[];
}

export interface TransactionConfiguration {
  txTimeoutOnPartitionMapExchange: number;
  deadlockTimeout: number;
  pessimisticTxLogSize: number;
  pessimisticTxLogLinger: number;
  txManagerFactory: null;
  useJtaSynchronization: boolean;
  defaultTxTimeout: number;
  txSerializableEnabled: boolean;
  defaultTxIsolation: string;
  defaultTxConcurrency: string;
  txManagerLookupClassName: null;
}

export interface UserAttributes {
  [key: string]: any;
}

export interface AtomicConfiguration {
  cacheMode: string;
  backups: number;
  affinity: null;
  groupName: null;
  atomicSequenceReserveSize: number;
}

export interface ConnectorConfiguration {
  jettyPath: null;
  secretKey: null;
  host: null;
  port: number;
  noDelay: boolean;
  idleTimeout: number;
  sslEnabled: boolean;
  sslClientAuth: boolean;
  sslFactory: null;
  portRange: number;
  threadPoolSize: number;
  receiveBufferSize: number;
  sendBufferSize: number;
  sslContextFactory: null;
  directBuffer: boolean;
  selectorCount: number;
  sendQueueLimit: number;
  idleQueryCursorCheckFrequency: number;
  messageInterceptor: null;
  idleQueryCursorTimeout: number;
}

export interface EncryptionSpi {
  masterKeyName: string;
  roleArn: string;
  externalId: string;
}

export interface NodeConfiguration {
  igniteInstanceName: string;
  stripedPoolSize: number;
  utilityCacheKeepAliveTime: number;
  igniteHome: string;
  nodeId: string;
  daemon: boolean;
  lifecycleBeans: null;
  clientMode: boolean;
  rebalanceThreadPoolSize: number;
  rebalanceTimeout: number;
  rebalanceThrottle: number;
  rebalanceBatchSize: number;
  cacheSanityCheckEnabled: boolean;
  failureDetectionTimeout: number;
  clientFailureDetectionTimeout: number;
  consistentId: null;
  activeOnStart: boolean;
  sqlSchemas: null;
  classLoader: null;
  localHost: null;
  authenticationEnabled: boolean;
  transactionConfiguration: TransactionConfiguration;
  queryThreadPoolSize: number;
  networkTimeout: number;
  failureHandler: FailureHandler;
  clientConnectorConfiguration: null;
  metricsLogFrequency: number;
  includeEventTypes: number[];
  userAttributes: UserAttributes;
  utilityCacheThreadPoolSize: number;
  publicThreadPoolSize: number;
  managementThreadPoolSize: number;
  communicationFailureResolver: null;
  dataStreamerThreadPoolSize: number;
  serviceThreadPoolSize: number;
  systemThreadPoolSize: number;
  asyncCallbackPoolSize: number;
  workDirectory: string;
  gridName: string;
  longQueryWarningTimeout: number;
  pluginConfigurations: null;
  executorConfiguration: null;
  sqlConnectorConfiguration: null;
  platformConfiguration: null;
  mvccVacuumThreadCount: number;
  lateAffinityAssignment: boolean;
  atomicConfiguration: AtomicConfiguration;
  mvccVacuumFrequency: number;
  sqlQueryHistorySize: number;
  networkCompressionLevel: number;
  segmentationPolicy: string;
  systemWorkerBlockedTimeout: null;
  peerClassLoadingEnabled: boolean;
  marshalLocalJobs: boolean;
  rebalanceBatchesPrefetchCount: number;
  segmentCheckFrequency: number;
  sslContextFactory: null;
  waitForSegmentOnStart: boolean;
  metricsUpdateFrequency: number;
  segmentationResolveAttempts: number;
  segmentationResolvers: null;
  networkSendRetryCount: number;
  metricsHistorySize: number;
  metricsExpireTime: number;
  networkSendRetryDelay: number;
  connectorConfiguration: ConnectorConfiguration;
  autoActivationEnabled: boolean;
  addressResolver: null;
  odbcConfiguration: null;
  serviceConfiguration: null;
  discoveryStartupDelay: number;
  cacheKeyConfiguration: null;
  binaryConfiguration: null;
  persistentStoreConfiguration: null;
  timeServerPortBase: number;
  memoryConfiguration: null;
  persistentStoreEnabled: boolean;
  timeServerPortRange: number;
  deploymentMode: string;
  includeProperties: null;
  warmupClosure: null;
  peerClassLoadingMissedResourcesCacheSize: number;
  peerClassLoadingLocalClassPathExclude: any[];
  peerClassLoadingThreadPoolSize: number;
  allSegmentationResolversPassRequired: boolean;
  encryptionSpi?: EncryptionSpi | null;
}
